// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode } from 'react';

import {
  type DateOnly,
  formatDate,
  type StrongOmit,
  type WorkOrderId,
  type WorkOrderStatusHistory,
  type WorkOrder,
  type WorkOrderStatusId,
} from '@evolvemep/api-ts';
import { Draggable } from '@hello-pangea/dnd';
import { Badge, Box, Card, type CardProps, Divider, Flex, OptionalPortal, Text } from '@mantine/core';

import type { ShopNavigationRoute } from 'app/ModuleRoutes';
import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { EntityStatusBadge } from 'components/Mantine/EntityStatusBadge';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import type { PageTitle } from 'components/Mantine/Navigation/types';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import type { StateNameFor, CustomStatusEntityName, StatusIdFor } from 'components/Mantine/StatusEditor/types';
import { lateColor } from 'constants/badgeMappingStatus';
import { isNotNil } from 'helpers/isNotNil';
import { EditableWorkOrderStatusBadge } from 'modules/Shop/WorkOrders/WorkOrderDetail/EditableWorkOrderStatusBadge';
import { getDaysRemaining } from 'modules/Shop/WorkOrders/WorkOrdersPage/common';

type Props<T extends CustomStatusEntityName> = {
  customStatusEntityName: T;
  currentPage: PageTitle;
  cardDestination: ShopNavigationRoute;
  projectName: string;
  facilityName?: string;
  stateName: StateNameFor<T>;
  statusId?: StatusIdFor<T>;
  needByDate: DateOnly | null | undefined;
  showOrigin?: 'project' | 'facility';
  includeStatusBadge?: boolean;
  includeEditableStatusBadge?: boolean;
  comment?: ReactNode;
  footer?: ReactNode;
  cardProps?: StrongOmit<CardProps, 'children'> & { ref?: React.Ref<HTMLDivElement> };
  /** Renders next to the status badge. */
  badges?: ReactNode;
  children: ReactNode;
  /** The index of the card in the list, used for drag-and-drop ordering. */
  index: number;
  /** Unique ID for the card. */
  id: string;
  /** Whether the card is draggable. Defaults to false. */
  draggable?: boolean;
  workOrderId: WorkOrderId;
  workOrderName: string;
} & (
  | (Pick<WorkOrder, 'workOrderId'> &
      WorkOrderStatusHistory & {
        editableStatus: boolean;
        onWorkOrderUpdated: (updatedWorkOrder: WorkOrder) => void;
      })
  | {
      /** Only Work Orders kanban cards support editable status atm */
      editableStatus?: false;
    }
);

/**
 * Must be used inside of a `DragDropContext`
 *
 * If you do not need Draggable, use `FakeDragDropContext`
 */
export const KanbanCard = <T extends CustomStatusEntityName>({
  currentPage,
  cardDestination,
  projectName,
  facilityName,
  children,
  customStatusEntityName,
  stateName,
  statusId,
  workOrderName,
  workOrderId,
  needByDate,
  comment,
  badges,
  footer,
  cardProps,
  showOrigin = 'project',
  draggable = false,
  index,
  id,
  ...props
}: Props<T>) => {
  const navigate = useEvolveNavigate(currentPage);
  const daysRemaining = getDaysRemaining(needByDate);
  const needByDateFormatted = formatDate(needByDate, 'MM/dd/yyyy');
  const isLate = daysRemaining < 0;

  const origin = showOrigin === 'project' ? projectName : facilityName;

  return (
    <Draggable index={index} draggableId={id} isDragDisabled={!draggable}>
      {(provided, snapshot) => (
        <OptionalPortal withinPortal={snapshot.isDragging}>
          <Card
            py="sm"
            mb="xs"
            withBorder
            radius="md"
            ref={provided.innerRef}
            shadow={snapshot.isDragging ? 'lg' : 'xs'}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            {...cardProps}
            style={{
              cursor: draggable ? undefined : 'pointer',
              ...provided.draggableProps.style,
              // TODO: `.style` can now be a function, need to handle it
              // oxlint-disable-next-line typescript/no-misused-spread
              ...cardProps?.style,
              flex: '0 0 auto',
              lineHeight: 1,
              userSelect: 'none',
            }}
            onClick={() => navigate(cardDestination)}
          >
            <Card.Section bg="gray.1">
              <Flex p="xs" pb={6} gap={4} fz="sm" align="center">
                <Text
                  title={origin}
                  c="dimmed"
                  style={{ minWidth: 40, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                >
                  {origin}
                </Text>
                <EvolveIcon icon="ArrowRight" size={8} color="gray.5" />
                <EvolveLink
                  className="kanban-work-order-link kanban-work-order-small-link"
                  to={`/work-orders/${workOrderId}`}
                  from={currentPage}
                >
                  <Text style={{ whiteSpace: 'nowrap' }} title={workOrderName}>
                    {workOrderName}
                  </Text>
                </EvolveLink>
              </Flex>
            </Card.Section>

            <Box my="sm">{children}</Box>

            <Flex gap="xs">
              {
                // TODO: This is fragile as it only works for work orders
                props.editableStatus && customStatusEntityName === 'Work Order' ? (
                  <EditableWorkOrderStatusBadge
                    editable
                    workOrder={{
                      workOrderId,
                      workOrderStatusId: (statusId as WorkOrderStatusId | undefined) ?? props.workOrderStatusId,
                      workOrderStateId: props.workOrderStateId,
                      workOrderStateName: props.workOrderStateName,
                    }}
                    onWorkOrderUpdated={props.onWorkOrderUpdated}
                  />
                ) : (
                  <EntityStatusBadge
                    customStatusEntityName={customStatusEntityName}
                    stateName={stateName}
                    statusId={statusId}
                  />
                )
              }
              {badges}
            </Flex>

            <Flex align="center" gap="xs" mt="xs">
              {isNotNil(needByDate) && (
                <AgGridStyleTooltip
                  c={isLate ? lateColor : 'gray.9'}
                  label={
                    <Flex align="center" gap="xs">
                      Need by
                      <Divider orientation="vertical" color={isLate ? lateColor : 'gray.9'} />
                      Days remaining
                    </Flex>
                  }
                  withArrow
                  openDelay={150}
                >
                  <Badge
                    size="lg"
                    variant="outline"
                    color={isLate ? lateColor : 'gray.4'}
                    c={isLate ? undefined : 'gray'}
                    bg={isLate ? 'red.0' : undefined}
                    radius="sm"
                    p={0}
                    fw={500}
                    style={{ cursor: 'initial' }}
                    onClick={(e) => e.stopPropagation()}
                    styles={{
                      label: {
                        display: 'flex',
                        flexDirection: 'row',
                        height: '100%',
                      },
                    }}
                  >
                    <Flex px={6}>{needByDateFormatted}</Flex>
                    <Divider orientation="vertical" opacity={0.4} />
                    <Flex bg={isLate ? lateColor : undefined} px={6} c={isLate ? 'gray.0' : undefined}>
                      {daysRemaining}
                    </Flex>
                  </Badge>
                </AgGridStyleTooltip>
              )}

              {comment ? (
                <AgGridStyleTooltip openDelay={100} label={comment} style={{ maxWidth: 300 }} withArrow>
                  <Badge
                    size="lg"
                    px={4}
                    radius="sm"
                    color="gray.4"
                    c="gray"
                    variant="outline"
                    style={{ cursor: 'initial' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <EvolveIcon icon="Comment" size="sm" color="gray.6" />
                  </Badge>
                </AgGridStyleTooltip>
              ) : null}
              {footer}
            </Flex>
          </Card>
        </OptionalPortal>
      )}
    </Draggable>
  );
};
